/**
 * MobileLanding — shown on screens < 1024 px (phones and tablets).
 *
 * Design intent: native app feel, not a website.
 *   · Solid brand-colored top section — mirrors iOS/Android app headers.
 *   · No marketing copy, no trust strips, no gradient hero scrolls.
 *   · Every tappable has active:scale press feedback (tactile feel).
 *   · touch-manipulation on all interactives removes the 300ms tap delay.
 *   · Bottom CTA bar is fixed — mirrors the primary action pattern of native apps.
 *   · Format tiles are full-width native-style cells, not web cards.
 */
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, UserRound, Users, Zap,
  BookOpen, ChevronRight, ArrowRight, X,
} from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import { cn } from "@/lib/utils";

// ── Data ──────────────────────────────────────────────────────────────────────

const formats = [
  {
    icon: UserRound,
    label: "1-on-1 Private Sessions",
    sub: "Personalised, your pace",
    to: "/private-tutoring-sessions",
    iconBg: "bg-[hsl(237,52%,31%)]",
    activeBorder: "active:border-[hsl(237,52%,31%)]/40",
  },
  {
    icon: Users,
    label: "Group Classes",
    sub: "Learn with peers, share costs",
    to: "/online-group-classes",
    iconBg: "bg-violet-600",
    activeBorder: "active:border-violet-400",
  },
  {
    icon: Zap,
    label: "Exam Crash Courses",
    sub: "Focused, time-bound prep",
    to: "/exam-crash-courses",
    iconBg: "bg-amber-500",
    activeBorder: "active:border-amber-400",
  },
  {
    icon: BookOpen,
    label: "Browse All Subjects",
    sub: "50+ subjects available",
    to: "/subjects",
    iconBg: "bg-emerald-600",
    activeBorder: "active:border-emerald-400",
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

export function MobileLanding() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  function handleSearch(q = query) {
    const trimmed = q.trim();
    if (!trimmed) return;
    navigate(`/search/one-to-one?q=${encodeURIComponent(trimmed)}`);
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex flex-col pb-24">

      {/* ── App bar (white) ────────────────────────────────────────────── */}
      <div className="bg-white px-5 pt-5 pb-4 flex items-center">
        <Logo size="md" />
      </div>

      {/* ── Colored hero section ────────────────────────────────────────── */}
      <div className="bg-[hsl(237,52%,28%)] px-5 pt-5 pb-8">
        {/* Headline */}
        <h1 className="text-[28px] sm:text-3xl font-extrabold text-white leading-tight tracking-tight mb-1">
          Find your perfect
          <br />
          <span className="text-amber-300">teacher.</span>
        </h1>
        <p className="text-sm text-white/60 mb-5">
          Verified tutors · all subjects · book in minutes
        </p>

        {/* Search input */}
        <div className="relative flex items-center bg-white rounded-2xl shadow-lg shadow-black/20 overflow-hidden">
          <Search className="absolute left-4 w-4 h-4 text-slate-400 shrink-0 pointer-events-none" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
            placeholder="Subject, exam, or skill…"
            className="w-full pl-11 pr-12 py-4 text-sm text-slate-800 bg-transparent outline-none placeholder:text-slate-400 touch-manipulation"
            autoCorrect="off"
            autoCapitalize="none"
          />
          {query ? (
            <button
              onClick={() => { setQuery(""); inputRef.current?.focus(); }}
              className="absolute right-11 touch-manipulation p-1"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          ) : null}
          <button
            onClick={() => handleSearch()}
            className="absolute right-2 w-9 h-9 rounded-xl bg-[hsl(237,52%,31%)] flex items-center justify-center touch-manipulation active:bg-[hsl(237,52%,22%)] transition-colors"
          >
            <ArrowRight className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────────────────── */}
      <div className="flex-1 -mt-3 rounded-t-3xl bg-[#F5F5F7] overflow-hidden">

        {/* Format tiles */}
        <div className="px-4 pt-5 pb-2">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3 px-1">
            What are you looking for?
          </p>
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#DCDDEE]">
            {formats.map((f, i) => (
              <button
                key={f.label}
                onClick={() => navigate(f.to)}
                className={cn(
                  "w-full flex items-center gap-4 px-4 py-3.5 touch-manipulation active:bg-slate-50 active:scale-[0.99] transition-all text-left",
                  i < formats.length - 1 && "border-b border-[#DCDDEE]",
                )}
              >
                <div className={cn(
                  "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
                  f.iconBg,
                )}>
                  <f.icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 text-sm leading-tight">{f.label}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{f.sub}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
              </button>
            ))}
          </div>
        </div>

        {/* Teacher CTA */}
        <div className="px-4 pt-3 pb-4">
          <button
            onClick={() => navigate("/become-an-online-tutor")}
            className="touch-manipulation w-full flex items-center gap-4 px-4 py-4 rounded-2xl bg-white border border-[#DCDDEE] shadow-sm active:bg-slate-50 active:scale-[0.99] transition-all text-left"
          >
            <div className="w-10 h-10 rounded-xl bg-[hsl(237,52%,31%)] flex items-center justify-center shrink-0">
              <UserRound className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-slate-800 text-sm leading-tight">Are you a teacher?</p>
              <p className="text-xs text-slate-400 mt-0.5">Register free and start earning</p>
            </div>
            <ArrowRight className="w-4 h-4 text-[hsl(237,52%,50%)] shrink-0" />
          </button>
        </div>

      </div>

      {/* ── Sticky bottom CTA bar ──────────────────────────────────────── */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-t border-[#DCDDEE] px-4 py-3 shadow-[0_-8px_32px_rgba(0,0,0,0.08)]">
        <div className="max-w-2xl mx-auto flex gap-3">
          <button
            onClick={() => navigate("/register")}
            className="touch-manipulation flex-1 bg-[hsl(237,52%,31%)] text-white font-bold text-sm py-4 rounded-2xl active:bg-[hsl(237,52%,22%)] active:scale-[0.98] transition-all shadow-lg shadow-[hsl(237,52%,31%)]/30"
          >
            Create free account
          </button>
          <button
            onClick={() => navigate("/login")}
            className="touch-manipulation flex-1 bg-slate-100 text-slate-700 font-bold text-sm py-4 rounded-2xl active:bg-slate-200 active:scale-[0.98] transition-all"
          >
            Log in
          </button>
        </div>
      </div>

    </div>
  );
}
